import { useState, useEffect, useRef } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { api } from '../../shared/api/client'
import { Header } from './Header'
import { Sidebar } from './Sidebar'
import ToastContainer from '../../components/Toast'
import ModalProvider from '../../components/ModalProvider'
import WhatsNewModal, { getLastSeenCommit, fetchChangelog } from '../../components/WhatsNewModal'
import { useBackgroundJobsStore, onDeployComplete } from '../../stores/backgroundJobsStore'
import { WebSocketProvider } from '../providers/WebSocketProvider'
import { TourProvider } from '../../tours/TourProvider'
import { canAccessPath, homePathFor } from '../access'
import { SlideInsertModalHost } from '../../features/slide-inserts/SlideInsertModal'
import { EntityNavigatorProvider } from '../../components/entity-view/EntityNavigator'
import { ArticleProfileProvider } from '../../features/article-profile'

const COLLAPSED_KEY = 'pim.sidebar.collapsed'

export function AppLayout() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated ?? false)
  const user = useAuthStore((s) => s.user)
  const token = useAuthStore((s) => s.token)
  const logout = useAuthStore((s) => s.logout)
  const setUser = useAuthStore((s) => s.setUser)
  const permissionsLoaded = useAuthStore((s) => s.permissionsLoaded)
  const navigate = useNavigate()
  const location = useLocation()
  const validatedTokenRef = useRef<string | null>(null)
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === '1'
    } catch {
      return false
    }
  })
  const [mobileOpen, setMobileOpen] = useState(false)
  const [showWhatsNew, setShowWhatsNew] = useState(false)

  const toggleCollapse = () => {
    setCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0')
      } catch {}
      return next
    })
  }

  useEffect(() => {
    if (!isAuthenticated) return
    fetchChangelog().then((data) => {
      if (data && data.commits.length > 0) {
        const latestHash = data.commits[0].hash
        if (getLastSeenCommit() !== latestHash) {
          setShowWhatsNew(true)
        }
      }
    })
  }, [isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated) return
    return onDeployComplete(() => {
      setShowWhatsNew(true)
    })
  }, [isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated) {
      validatedTokenRef.current = null
      navigate('/login', { replace: true })
      return
    }
    if (token && !user) {
      logout()
      navigate('/login', { replace: true })
    }
  }, [isAuthenticated, token, user, logout, navigate])

  // localStorage подтверждает лишь наличие старой сессии. При каждом монтировании
  // защищённого layout один раз сверяем её с сервером, где учитывается is_active.
  useEffect(() => {
    if (!isAuthenticated || !token || validatedTokenRef.current === token) return

    validatedTokenRef.current = token
    const controller = new AbortController()

    api.get('/auth/me', { signal: controller.signal, timeout: 30_000 })
      .then((response) => {
        if (controller.signal.aborted) return
        const profile = response.data?.data?.user ?? response.data?.user
        if (profile) setUser(profile)
      })
      .catch((error: any) => {
        if (controller.signal.aborted || error?.response?.status !== 401) return
        logout()
        navigate('/login', { replace: true })
      })

    return () => {
      controller.abort()
      if (validatedTokenRef.current === token) {
        validatedTokenRef.current = null
      }
    }
  }, [isAuthenticated, token, logout, navigate, setUser])

  // The root dashboard is not universal; route users to the first permitted workspace.
  useEffect(() => {
    const permissions = user?.permissions ?? []
    if (
      isAuthenticated
      && permissionsLoaded
      && location.pathname === '/'
      && !canAccessPath(location.pathname, permissions)
    ) {
      navigate(homePathFor(permissions), { replace: true })
    }
  }, [isAuthenticated, permissionsLoaded, user?.permissions, location.pathname, navigate])

  if (!isAuthenticated) return null

  return (
    <TourProvider>
      <WebSocketProvider>
        <EntityNavigatorProvider>
          <div className="flex h-screen bg-gray-50 dark:bg-zinc-950 overflow-hidden">
            <Sidebar
              collapsed={collapsed}
              mobileOpen={mobileOpen}
              onCloseMobile={() => setMobileOpen(false)}
            />

            <div className="flex-1 flex flex-col min-w-0">
              <Header
                collapsed={collapsed}
                onToggleCollapse={toggleCollapse}
                onOpenMobileSidebar={() => setMobileOpen(true)}
                onShowWhatsNew={() => setShowWhatsNew(true)}
              />

              <main className="flex-1 overflow-auto p-6 dark:bg-zinc-950">
                <ArticleProfileProvider>
                  <Outlet />
                </ArticleProfileProvider>
              </main>
            </div>

            <ToastContainer
              onJobClick={(jobId: string) => {
                const { jobs } = useBackgroundJobsStore.getState()
                const job = jobs.get(jobId)
                if (job) {
                  // Toast click — JobProgressModal handled in Header
                }
              }}
            />
            <ModalProvider />
            <SlideInsertModalHost />
            <WhatsNewModal isOpen={showWhatsNew} onClose={() => setShowWhatsNew(false)} />
          </div>
        </EntityNavigatorProvider>
      </WebSocketProvider>
    </TourProvider>
  )
}
